<template>
  <div
    class="tw:flex tw:flex-col tw:overflow-hidden tw:rounded-[12px] tw:bg-white tw:p-px tw:shadow-md"
    data-testid="card"
    :style="cardStyle"
  >
    <div
      class="tw:flex-cols ai-background-gradient tw:flex tw:flex-1 tw:overflow-hidden"
      :class="{ 'ai-animated-gradient': aiSummary.loading }"
    >
      <div
        class="tw:round-[10px] tw:relative tw:flex tw:flex-1 tw:flex-col tw:gap-2 tw:overflow-hidden"
      >
        <div class="tw:flex tw:justify-between tw:p-2">
          <div>
            <BaseInputDatePicker
              bordered
              circle
              close-on-update
              date-style="short"
              :disabled="aiSummary.loading || undefined"
              flat
              :label="datePickerLabel"
              limit-to-past-year
              :range="aiSummary.dateRange"
              :range-options="rangeOptions"
              range-selection="custom"
              :range-span-limit="30"
              show-date-input
              show-range-options
              show-time-input
              show-update-button
              @update:range="updateRange"
            />
          </div>
          <div>
            <BaseButton
              :aria-label="
                getLocaleText('ai_summary_card.expand_button_tooltip')
              "
              circle
              data-testid="expand-button"
              flat
              icon="expand"
              new-design
              new-icon
              @click="expand"
            >
              <BaseStreemTooltip>
                {{ getLocaleText("ai_summary_card.expand_button_tooltip") }}
              </BaseStreemTooltip>
            </BaseButton>
            <BaseButton
              :aria-label="getLocaleText('global.close')"
              circle
              data-testid="close-button"
              flat
              icon="close"
              new-design
              new-icon
              @click="close"
            />
          </div>
        </div>

        <Transition
          appear
          mode="out-in"
          name="fade"
        >
          <div
            v-if="aiSummary.loading"
            class="tw:flex tw:flex-1 tw:items-center tw:justify-center"
          >
            <AIIcon
              active
              class="tw:h-8 tw:w-8"
            />
          </div>
          <div
            v-else-if="aiSummary.result"
            class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden"
          >
            <div class="ai-scrollbar tw:flex-1 tw:overflow-auto tw:px-4">
              <template
                v-for="topic in aiSummary.result.topics"
                :key="topic.id"
              >
                <h3 class="tw:mb-2 tw:text-base tw:font-bold tw:leading-tight">
                  {{ topic.title }}
                </h3>
                <p class="tw:leading-tight">
                  {{ topic.summary }}
                </p>
              </template>
            </div>
            <AISummaryStreamActions
              v-if="aiSummaryStreamId"
              card
              :stream-id="aiSummaryStreamId"
            />
          </div>
          <div v-else-if="aiSummary.error">
            <div
              v-if="aiSummary.status === 422"
              class="tw:flex tw:gap-2 tw:px-4"
            >
              <AIIcon class="tw:shrink-0" />
              <div class="tw:ml-1">
                {{ getLocaleText("ai_summary.not_enough_mentions") }}
              </div>
            </div>
            <div
              v-else
              class="tw:flex tw:gap-2 tw:px-4"
            >
              <BaseIcon
                class="tw:text-lg tw:text-orange-500"
                icon="exclamation-circle"
              />
              <div class="tw:ml-1 tw:text-orange-500">
                {{ getLocaleText("ai_summary.summary_error") }}
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { executeTransition, TransitionPresets } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, type CSSProperties, ref, watch } from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  BaseInputDatePicker,
  BaseStreemTooltip,
} from "shared/components/base";
import DateRange from "shared/helpers/DateRange";
import AIIcon from "shared/modules/AI/components/AIIcon/AIIcon.vue";
import {
  type AISummary,
  useAIStore,
} from "shared/modules/AI/stores/useAIStore";
import AISummaryStreamActions from "shared/modules/AISummary/components/AISummaryStreamActions/AISummaryStreamActions.vue";
import { useUserStore } from "shared/stores/user";
import { type Nullable } from "shared/types/utility";

const CARD_HEIGHT = 420;
const CARD_TRANISITION_DURATION = 300;

interface AISummaryCardProps {
  bottomOffset?: number;
  streamId: number;
}

const props = withDefaults(defineProps<AISummaryCardProps>(), {
  bottomOffset: 8,
});

const aiStore = useAIStore();

const { getAISummary, loadAISummary, openAIModal, setAISummary } = aiStore;

const { aiSummaryStreamId, aiModalOpen } = storeToRefs(aiStore);

const userStore = useUserStore();
const { betaEnabled } = storeToRefs(userStore);

const expanded = ref<boolean>(false);
const cardHeight = ref<number>(0);

const cardStyle = computed<CSSProperties>(() => ({
  height: `${cardHeight.value}px`,
  marginBottom: expanded.value ? `${props.bottomOffset}px` : "0px",
}));

const aiSummary = computed<Required<AISummary>>(() =>
  getAISummary(props.streamId)
);

const isCurrentAIStream = computed(
  () => aiSummaryStreamId.value === props.streamId
);

const datePickerLabel = computed<string>(() =>
  aiSummary.value.dateRangeUpdated
    ? ""
    : aiSummary.value.dateRange.prettyRangeText
);

const rangeOptions = computed<DateRange[]>(() => [
  DateRange.today(),
  DateRange.yesterday(),
  DateRange.lastThreeDays(),
  DateRange.lastSevenDays(),
  DateRange.lastFourteenDays(),
  DateRange.lastThirtyDays(),
]);

async function animatePanel(from: number, to: number) {
  const isExpanded = expanded.value;

  await executeTransition(cardHeight, from, to, {
    duration: CARD_TRANISITION_DURATION,
    transition: TransitionPresets.easeInOutCubic,
    abort() {
      return isExpanded !== expanded.value;
    },
  });
}

function openPanel() {
  expanded.value = true;

  return animatePanel(cardHeight.value, CARD_HEIGHT);
}

function closePanel() {
  expanded.value = false;

  return animatePanel(cardHeight.value, 0);
}

function expand() {
  track("Clicked Open in Assistant button", { streamId: props.streamId });

  closePanel().then(() => {
    openAIModal("AISummaryPanel");
  });
}

function close() {
  closePanel().then(() => {
    aiSummaryStreamId.value = null;
  });
}

function reload() {
  loadAISummary(props.streamId, betaEnabled.value);
}

function updateRange(range: Nullable<DateRange>) {
  if (!range) return;
  setAISummary(props.streamId, { dateRange: range, dateRangeUpdated: true });
  reload();
}

watch(
  isCurrentAIStream,
  async () => {
    if (isCurrentAIStream.value && !aiModalOpen.value) {
      await openPanel();
      if (aiSummary.value.result === null)
        loadAISummary(props.streamId, betaEnabled.value);
    } else if (!isCurrentAIStream.value) {
      closePanel();
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => aiModalOpen.value,
  () => {
    if (isCurrentAIStream.value && !aiModalOpen.value) {
      openPanel();
    }
  }
);
</script>

<style scoped lang="scss">
@import "shared/css/components/ai";
</style>
